// Render-time reads the voice surfaces take from the app shell. Bound once
// from `src/composition/hosts/agents/agents-data-wiring.ts`, which `_app` already evaluates.
// Every member is a call-through: nothing here reads an app binding, and
// nothing here imports the app or a `/ui` module.

import type { Region } from '@heidi/schema/ml-scribe';
import type { QueryClient } from '@tanstack/react-query';

import type { VoiceWorkTaskFailureReason } from '../../contracts/voice-protocol';

interface VoiceRoute {
  pathname: string;
  asPath: string;
  query: Record<string, string | string[] | undefined>;
}

/** A computer task the voice server created a project for and handed to this desktop. */
interface VoiceWorkTurn {
  projectId: string;
  agentRunId: string;
  /** Server-minted; the turn must be submitted under exactly this id. */
  turnId: string;
  task: string;
}

type VoiceWorkTurnResult =
  | { ok: true }
  | { ok: false; reason: VoiceWorkTaskFailureReason };

export interface VoiceAppHost {
  subscribeRouteChange: (listener: () => void) => () => void;
  readRoute: () => VoiceRoute;
  usePathname: () => string;
  getQueryClient: () => QueryClient;
  openEvidenceSidebar: () => void;
  useIsEvidenceSidebarOpen: () => boolean;
  isVisitRecordingActive: () => boolean;
  useIsVisitRecordingActive: () => boolean;
  useIsVoiceDictationModeEnabled: () => boolean;
  isProductFeedbackAvailable: () => boolean;
  /** Submits a voice-started computer task through the desktop Work path. Never throws. */
  submitVoiceWorkTurn: (turn: VoiceWorkTurn) => Promise<VoiceWorkTurnResult>;
  /**
   * The authenticated Kinde region from the query cache, or undefined while it
   * is unresolved. Never the URL/fallback region, which fails open.
   */
  readAuthenticatedRegion: () => Promise<Region | undefined>;
}

let host: VoiceAppHost | null = null;

export const configureVoiceAppHost = (next: VoiceAppHost): void => {
  host = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getVoiceAppHost = (): VoiceAppHost => {
  if (!host) {
    throw new Error(
      '[agents/voice-app-host] not configured — src/composition/hosts/agents/agents-data-wiring.ts binds it'
    );
  }
  return host;
};

/** Test-only reset so a suite cannot leak a double into the next one. */
export const resetVoiceAppHostForTests = (): void => {
  host = null;
};
